import type { ReactNode } from "react";
import { EmptyState } from "./EmptyState";
import styles from "./Table.module.css";

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  /** Kort label, der vises ved cellen i den valgfrie mobile kortvisning. */
  mobileLabel?: string;
  /** Kolonnejustering — tal og handlinger højrestilles (desktop-håndværk). */
  align?: "left" | "right";
}

export interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Tom-tilstand er indbygget: vises automatisk når rows er tom. */
  emptyTitle?: ReactNode;
  emptyDescription?: ReactNode;
  emptyCta?: ReactNode;
  /** Bevar tabellen på desktop, men vis hver række som et læsbart kort på små skærme. */
  mobileCards?: boolean;
}

export function Table<T>({
  columns,
  rows,
  rowKey,
  emptyTitle = "Ingen data endnu",
  emptyDescription,
  emptyCta,
  mobileCards = false,
}: TableProps<T>) {
  if (rows.length === 0) {
    return (
      <div className={styles.wrap}>
        <EmptyState
          title={emptyTitle}
          description={emptyDescription}
          cta={emptyCta}
        />
      </div>
    );
  }

  return (
    <div className={`${styles.wrap} ${mobileCards ? styles.cardWrap ?? "" : ""}`}>
      <table className={`${styles.table} ${mobileCards ? styles.cardTable ?? "" : ""}`}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={col.align === "right" ? styles.right : undefined}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={col.align === "right" ? styles.right : undefined}
                  data-column={col.key}
                  data-label={col.mobileLabel}
                >
                  {col.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
